import { Box, Button, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Link } from "gatsby";
import React from "react";
import useExpressDeliveryAvailability from "../../../../../hooks/use-express-delivery-availability";
import useLocalizedLocationPath from "../../../../../hooks/use-localized-location-path";
import { ProductType } from "../../../../../types/airgraft";
import GeoPinIcon from "../../../../../components/icons/geo-pin";
import StoreIcon from "../../../../../components/icons/store";
import ProductQuantitySelector from "../../../ProductQuantitySelector";
import {
  StoreLink,
  StoreTextButton,
  AddressTextButton,
  PRODUCT_DETAILS_ACTIONS_HEIGHT
} from "./styles";
import ExpressDeliveryLogo from "../../../../express-delivery/ExpressDeliveryLogo";
import DeliveryCoverageInfo from "../../../../express-delivery/DeliveryCoverageInfo";
import deepEqual from "react-fast-compare";
import { getDeliveryAddressShortName } from "../../../../../helpers/express-delivery/address";

type Props = {
  product: ProductType;
};

function ProductDetailsActions({ product }: Props) {
  const getLocalizedPath = useLocalizedLocationPath();

  const {
    availableProvider,
    getProductExpressAvailability,
    isLoadingAvailableProducts,
    isLoadingAvailableProvider,
    address,
    toggleAvailabilityPanel,
    getAvailableProviderProductData
  } = useExpressDeliveryAvailability();

  const isLoadingAvailability =
    isLoadingAvailableProducts || isLoadingAvailableProvider;
  const availability = getProductExpressAvailability(product, {
    fallbackToDefault: true
  });

  // Actions skeleton while loading availability
  if (isLoadingAvailability && availability !== "COMING_SOON") {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems={{ xs: "center", sm: "flex-start" }}
      >
        <Skeleton
          variant="rect"
          style={{
            width: "100%",
            height: PRODUCT_DETAILS_ACTIONS_HEIGHT,
            borderRadius: PRODUCT_DETAILS_ACTIONS_HEIGHT
          }}
        />
        <Box width="100%" mt={{ xs: 0.5, sm: 1 }}>
          <Skeleton
            variant="rect"
            style={{ width: "65%", height: "14px", borderRadius: "14px" }}
          />
        </Box>
        <Box width="100%" mt={{ xs: 0.5, sm: 1 }}>
          <Skeleton
            variant="rect"
            style={{ width: "45%", height: "14px", borderRadius: "14px" }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={{ xs: "center", sm: "flex-start" }}
    >
      {/* AGX (Available, quantity selector) */}
      {availability === "AGX" && availableProvider && (
        <Box alignSelf="stretch">
          <ProductQuantitySelector
            product={product}
            maxQuantity={
              getAvailableProviderProductData(product)?.availableQuantity || 0
            }
          />

          <Box mt={1} textAlign={{ xs: "center", sm: "left" }}>
            <Typography variant="body2">
              {`${
                availableProvider
                  ? `Fulfilled by ${availableProvider.publicName}. `
                  : ""
              }`}
              {"Delivering to "}
              <AddressTextButton onClick={() => toggleAvailabilityPanel(true)}>
                {getDeliveryAddressShortName(address)}
              </AddressTextButton>
              .
            </Typography>
          </Box>
        </Box>
      )}

      {/* AGX (Not available for current provider) */}
      {availability === "IN_STORE" && address && (
        <Button
          variant="contained"
          fullWidth
          startIcon={<StoreIcon fontSize="small" />}
          component={Link}
          to={getLocalizedPath("/store-locator")}
          style={{ minHeight: PRODUCT_DETAILS_ACTIONS_HEIGHT }}
          data-analytics="product-details.find-store"
        >
          Find in a dispensary near you
        </Button>
      )}

      {/* AGX (Not available, No Address) */}
      {availability === "AGX" && !address && (
        <>
          <Button
            variant="contained"
            fullWidth
            color="primary"
            onClick={() => toggleAvailabilityPanel()}
            className="express-delivery"
            endIcon={<ExpressDeliveryLogo height="10px" variant="white" />}
            style={{ minHeight: PRODUCT_DETAILS_ACTIONS_HEIGHT }}
            data-testid="express-delivery-add-location"
            data-analytics="product-details.get-it-delivered"
          >
            Get it delivered with
          </Button>

          <Box mt={1}>
            <DeliveryCoverageInfo />
          </Box>
        </>
      )}

      {/* Coming soon */}
      {availability === "COMING_SOON" && (
        <>
          <Box
            width="100%"
            height={PRODUCT_DETAILS_ACTIONS_HEIGHT}
            bgcolor="common.white"
            borderRadius={PRODUCT_DETAILS_ACTIONS_HEIGHT}
            fontWeight={600}
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              userSelect: "none"
            }}
          >
            Coming Soon
          </Box>
        </>
      )}

      {/* Store locator secondary link */}
      {availability === "AGX" && (
        <Box mt={{ xs: 1, sm: 2 }}>
          <StoreLink
            to={getLocalizedPath("/store-locator")}
            data-analytics="product-details.also-available-in-store"
          >
            <StoreIcon fontSize="small" />
            Also available in-store
          </StoreLink>
        </Box>
      )}

      {/* Not available for delivery with current provider */}
      {availability === "IN_STORE" && address && availableProvider && (
        <Box mt={2} textAlign={{ xs: "center", sm: "left" }}>
          <Typography variant="body2">
            This product is not available for delivery at
            <AddressTextButton onClick={() => toggleAvailabilityPanel(true)}>
              <GeoPinIcon fontSize="small" />
              {getDeliveryAddressShortName(address)}
            </AddressTextButton>
            {". "}
            <StoreTextButton onClick={() => toggleAvailabilityPanel(true)}>
              Try a different address
            </StoreTextButton>
          </Typography>
        </Box>
      )}

      {/* Not available for delivery (No available provider, out of range) */}
      {availability === "IN_STORE" && address && !availableProvider && (
        <Box mt={2} textAlign={{ xs: "center", sm: "left" }}>
          <Typography variant="body2">
            <AddressTextButton onClick={() => toggleAvailabilityPanel(true)}>
              <GeoPinIcon fontSize="small" />
              {getDeliveryAddressShortName(address)}
            </AddressTextButton>
            {" is out of delivery range. "}
            <StoreTextButton onClick={() => toggleAvailabilityPanel(true)}>
              Try a different address
            </StoreTextButton>
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(ProductDetailsActions, deepEqual);
